import React, { useState } from 'react';
import { supabase } from '../supabase';
import AuthShell from './AuthShell';
import {
  EMAIL_RE,
  friendlyAuthError,
  inputStyle, inputFocusStyle, inputErrorStyle,
  labelStyle, inlineErrorStyle, primaryButtonStyle, linkStyle,
} from './authUtils';
import { colors, spacing, fontStack } from '../design-system/tokens';

/**
 * /forgot-password — request a password reset link.
 *
 * Sends the user a magic link via Supabase. For security we always
 * show the same success state, regardless of whether the email
 * actually has an account.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [focus, setFocus] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  const emailTrim = email.trim();
  const emailValid = EMAIL_RE.test(emailTrim);
  const showEmailError = touched && email.length > 0 && !emailValid;

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!emailValid || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const redirectTo =
        typeof window !== 'undefined'
          ? `${window.location.origin}/reset-password`
          : undefined;
      const { error: e2 } = await supabase.auth.resetPasswordForEmail(emailTrim, { redirectTo });
      if (e2) throw e2;
      // Show success state. We deliberately show this even if the email
      // doesn't have an account, so we don't reveal account existence.
      setSentEmail(emailTrim);
      setSent(true);
    } catch (err) {
      // Same security stance: only show the error if it's network-level.
      // Auth-level "user not found" should still surface the success path.
      const msg = String(err?.message || '').toLowerCase();
      const isNetwork = msg.includes('failed to fetch') || msg.includes('networkerror');
      const isRateLimit = msg.includes('rate limit') || msg.includes('429');
      if (isNetwork || isRateLimit) {
        setError(friendlyAuthError(err?.message));
        setSubmitting(false);
        return;
      }
      // For any other error, still show success (don't leak existence).
      setSentEmail(emailTrim);
      setSent(true);
    }
  };

  if (sent) {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center' }}>
          <SuccessGlyph />
          <h1 style={titleStyle}>Check your email</h1>
          <p style={{ ...subStyle, marginTop: 10 }}>
            If an account exists for{' '}
            <strong style={{ color: colors.textPrimary }}>{sentEmail}</strong>,
            we've sent a reset link.
          </p>
          <p
            style={{
              margin: `${spacing.base}px 0 0`,
              fontSize: 13,
              color: colors.textSecondary,
              fontFamily: fontStack,
            }}
          >
            Check spam if you don't see it within 5 minutes.
          </p>
          <p
            style={{
              margin: `${spacing.lg}px 0 0`,
              fontSize: 14,
              color: colors.textSecondary,
              fontFamily: fontStack,
            }}
          >
            <a href="/login" style={linkStyle}>← Back to sign in</a>
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 style={titleStyle}>Reset your password</h1>
      <p style={subStyle}>
        Enter your email and we'll send you a link to set a new password.
      </p>

      <form
        onSubmit={submit}
        noValidate
        style={{ marginTop: spacing.lg, display: 'flex', flexDirection: 'column', gap: spacing.base }}
      >
        <div>
          <label htmlFor="forgot-email" style={labelStyle}>Email</label>
          <input
            id="forgot-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => { setTouched(true); setFocus(false); }}
            onFocus={() => setFocus(true)}
            placeholder="you@example.com"
            required
            style={{
              ...inputStyle,
              ...(focus ? inputFocusStyle : null),
              ...(showEmailError ? inputErrorStyle : null),
            }}
          />
          {showEmailError && <p style={inlineErrorStyle}>Please enter a valid email address.</p>}
        </div>

        {error && <p style={{ ...inlineErrorStyle, fontSize: 13, marginTop: 0 }}>{error}</p>}

        <button
          type="submit"
          disabled={!emailValid || submitting}
          style={primaryButtonStyle(!emailValid, submitting)}
        >
          {submitting ? 'Sending…' : 'Send reset link'}
        </button>
      </form>

      <p
        style={{
          margin: `${spacing.lg}px 0 0`,
          textAlign: 'center',
          color: colors.textSecondary,
          fontSize: 14,
          fontFamily: fontStack,
        }}
      >
        Remember your password?{' '}
        <a
          href="/login"
          style={linkStyle}
          onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
        >
          Sign in →
        </a>
      </p>
    </AuthShell>
  );
}

const titleStyle = {
  margin: 0,
  fontSize: 28,
  fontWeight: 600,
  color: colors.textPrimary,
  letterSpacing: '-0.01em',
  fontFamily: fontStack,
};

const subStyle = {
  margin: '6px 0 0',
  fontSize: 16,
  color: colors.textSecondary,
  fontFamily: fontStack,
  lineHeight: 1.5,
};

function SuccessGlyph() {
  return (
    <div
      aria-hidden="true"
      style={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        backgroundColor: 'rgba(111, 207, 151, 0.18)',
        color: 'var(--success)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 18,
      }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
  );
}
